<template>
  <div class="create-job-post-step1 d-flex flex-column h-100">
    <div class="step-header pa-6 pb-4">
      <h1 class="text-h1 text-textDarkest">
        Write a Job Post
      </h1>
      <div class="text-body-2 text-textLight mt-1">
        Fill in the details below. Use the search to find similar job posts for reference.
      </div>
    </div>

    <v-divider class="opacity-100" />

    <div class="step-body flex-grow-1 overflow-hidden">
      <v-row
        no-gutters
        class="h-100"
      >
        <!-- Left: form inputs (20%) -->
        <v-col
          class="pa-6 d-flex flex-column ga-4 border-e left-column"
          cols="3"
        >
          <v-form
            ref="formRef"
            v-model="formValid"
          >
            <div class="d-flex flex-column ga-4">
              <DpplTextField
                v-model="formTitle"
                label="Job Post Title"
                outlined
                :rules="titleRules"
                dense
              />
              <DpplTextField
                v-model="formLocation"
                label="Location"
                outlined
                :rules="locationRules"
                dense
              />
              <DpplTextField
                v-model="formJobCode"
                label="Job ID (optional)"
                outlined
                dense
              />
            </div>
          </v-form>

          <v-spacer />

          <v-btn
            color="primary"
            variant="flat"
            rounded="lg"
            :disabled="!formValid"
            @click="onNext"
          >
            Next Step
          </v-btn>
        </v-col>

        <!-- Middle: search results (30%) -->
        <v-col
          class="border-e overflow-y-auto h-100 middle-column"
          cols="4"
        >
          <div
            id="similar-jobs-heading"
            class="pa-4 pb-2 text-caption text-textDark text-uppercase font-weight-medium"
          >
            Similar job posts
          </div>
          <JobPostSearchResults
            :results="store.searchResults"
            :loading="store.searchLoading"
            :selected-id="store.selectedJobId"
            :has-searched="hasSearched"
            @select="store.selectJobForPreview($event)"
          />
        </v-col>

        <!-- Right: job post preview (50%) -->
        <v-col
          class="overflow-y-auto h-100 right-column"
          cols="5"
        >
          <WizardJobPreview
            :job="store.previewJob ? {
              ...store.previewJob,
              title: formTitle,
              location: formLocation,
              job_code: formJobCode,
            } : null"
            :loading="store.previewLoading"
          />
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue';
import { debounce } from 'lodash-es';
import { storeToRefs } from 'pinia';
import { useCreateJobPostWizard } from '@/stores/jobPosts/createJobPostWizard';
import DpplTextField from '@/components/datapeople/DpplTextField.vue';
import validation from '@/helpers/validation';
import JobPostSearchResults from './JobPostSearchResults.vue';
import WizardJobPreview from './WizardJobPreview.vue';

const store = useCreateJobPostWizard();
const { formTitle, formJobCode, formLocation } = storeToRefs(store);

const formRef = ref(null);
const formValid = ref(false);
const hasSearched = ref(false);

const titleRules = validation.titleRules();
const locationRules = validation.locationRules();

const debouncedSearch = debounce(async () => {
  hasSearched.value = true;
  await store.searchJobPosts();
}, 1000);

watch([formTitle, formJobCode], () => {
  debouncedSearch();
});

const onNext = async () => {
  const result = await formRef.value?.validate();
  if (result?.valid) {
    store.goToStep2();
  }
};
</script>
